export const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
export const moneyWhole = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const wholeNumber = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
/** A catalogue price with its currency spelled out ("$1,250 USD"), since destinations are sold in either MXN or USD. */
export const priceWithCurrency = (amount: number, currency: string) => `$${wholeNumber.format(amount)} ${currency}`;
const longDate = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "long", year: "numeric" });
const mediumDate = new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" });
const timestamp = new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const weekday = new Intl.DateTimeFormat("es-MX", { weekday: "short", day: "numeric", month: "short" });

/** A `YYYY-MM-DD` calendar date, read in local time so it never shifts a day across time zones. */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function formatCalendarDate(value: string | null | undefined, style: "long" | "medium" = "medium"): string {
  const parsed = parseCalendarDate(value);
  if (!parsed) return "—";
  return (style === "long" ? longDate : mediumDate).format(parsed).replace(".", "");
}

export function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Fecha no disponible" : timestamp.format(parsed);
}

export function formatShortDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Fecha no disponible" : weekday.format(parsed);
}

export function relativeTime(value: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return "hace un momento";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)} h`;
  return `hace ${Math.floor(seconds / 86_400)} d`;
}

/** Whole months between today and a target date, never below 1. */
export function monthsUntil(value: string | null | undefined): number {
  const target = parseCalendarDate(value);
  if (!target) return 0;
  const now = new Date();
  const months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth()) - (target.getDate() < now.getDate() ? 1 : 0);
  return Math.max(1, months);
}

export function daysUntil(value: string | null | undefined): number | null {
  const target = parseCalendarDate(value);
  if (!target) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Savings plan for a goal: the monthly contribution that reaches `goal` in `months` at the annual
 * rate (compounding monthly), and the share of the goal the yield covers. The rate is the product's
 * quoted rate and the result is an estimate — variable yield is stated wherever it is shown.
 */
export function savingsPlan(goal: number, months: number, annualRatePercent: number) {
  if (!goal || goal <= 0 || months <= 0) return { monthly: 0, interest: 0, contributed: 0 };
  const i = annualRatePercent / 100 / 12;
  const monthly = i > 0 ? (goal * i) / (Math.pow(1 + i, months) - 1) : goal / months;
  const contributed = monthly * months;
  return { monthly: Math.ceil(monthly), interest: Math.max(0, Math.round(goal - contributed)), contributed: Math.round(contributed) };
}

export function initials(name: string | undefined | null): string {
  if (!name) return "A";
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "A";
}

export function todayIso(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
